import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { QuestionTypeSelector } from './QuestionTypeSelector';
import { MultipleChoiceEditor } from './question-editors/MultipleChoiceEditor';
import { FillBlankEditor } from './question-editors/FillBlankEditor';
import { MixMatchEditor } from './question-editors/MixMatchEditor';
import { ShortAnswerEditor } from './question-editors/ShortAnswerEditor';
import { LongAnswerEditor } from './question-editors/LongAnswerEditor';
import { MathEditor } from './question-editors/MathEditor';
import type { NewQuestion, QuestionType, Difficulty, QuestionContent } from '@/types';
import CreatePencilRuleIcon from '@/assets/common/create-pencil-ruler.svg?react';
import styles from './QuestionEditorPanel.module.scss';

interface QuestionEditorPanelProps {
  question: Partial<NewQuestion> | null;
  onSave: (question: NewQuestion) => void;
  onCancel: () => void;
  isEditMode: boolean;
  showMath?: boolean;
}

export function QuestionEditorPanel({
  question,
  onSave,
  onCancel,
  isEditMode,
  showMath = false,
}: QuestionEditorPanelProps) {
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState<QuestionType>('multiple_choice');
  const [content, setContent] = useState<QuestionContent | null>(null);
  const [explanation, setExplanation] = useState('');
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [tags, setTags] = useState<string[]>([]);

  // Load question data when editing
  useEffect(() => {
    if (question) {
      setQuestionText(question.question_text || '');
      setQuestionType(question.question_type || 'multiple_choice');
      setContent(question.content || null);
      setExplanation(question.explanation || '');
      setDifficulty(question.difficulty || 'medium');
      setTags(question.tags || []);
    } else {
      // Reset form for new question
      setQuestionText('');
      setQuestionType('multiple_choice');
      setContent(null);
      setExplanation('');
      setDifficulty('medium');
      setTags([]);
    }
  }, [question]);

  const handleTypeChange = (newType: QuestionType) => {
    setQuestionType(newType);
    // Reset content when type changes
    setContent(null);
  };

  const handleSave = () => {
    if (!content || !questionText.trim() || !explanation.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const newQuestion: NewQuestion = {
      question_text: questionText.trim(),
      question_type: questionType,
      content,
      explanation: explanation.trim(),
      difficulty,
      order_index: question?.order_index || 0,
      tags,
    };

    onSave(newQuestion);
  };

  const isValid = questionText.trim() !== '' && content !== null && explanation.trim() !== '';

  if (!question) {
    return (
      <div className={styles.editor}>
        <div className={styles.editor__empty}>
          <motion.div
            className={styles.editor__emptyIcon}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <CreatePencilRuleIcon width={48} height={48} />
          </motion.div>
          <h3 className={styles.editor__emptyTitle}>No Question Selected</h3>
          <p className={styles.editor__emptyText}>
            Click "Add Question" or select an existing question to edit
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.editor}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <h3 className={styles.editor__title}>
        {isEditMode ? 'Edit Question' : 'New Question'}
      </h3>

      {/* Question Type Selector */}
      <QuestionTypeSelector value={questionType} onChange={handleTypeChange} showMath={showMath} />

      {/* Question Text */}
      <div className={styles.editor__field}>
        <label htmlFor="question-text" className={styles.editor__label}>
          Question Text <span className={styles.editor__required}>*</span>
        </label>
        <textarea
          id="question-text"
          className={styles.editor__textarea}
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="Enter your question here..."
          rows={3}
          maxLength={1000}
        />
      </div>

      {/* Type-specific Content Editor */}
      <div className={styles.editor__contentSection}>
        {questionType === 'multiple_choice' && (
          <MultipleChoiceEditor content={content} onChange={setContent} />
        )}
        {questionType === 'fill_blank' && (
          <FillBlankEditor
            content={content}
            questionText={questionText}
            onChange={setContent}
          />
        )}
        {questionType === 'mix_match' && (
          <MixMatchEditor content={content} onChange={setContent} />
        )}
        {questionType === 'short_answer' && (
          <ShortAnswerEditor content={content} onChange={setContent} />
        )}
        {questionType === 'long_answer' && (
          <LongAnswerEditor content={content} onChange={setContent} />
        )}
        {questionType === 'math' && (
          <MathEditor content={content} onChange={setContent} />
        )}
      </div>

      {/* Explanation */}
      <div className={styles.editor__field}>
        <label htmlFor="explanation" className={styles.editor__label}>
          Explanation <span className={styles.editor__required}>*</span>
        </label>
        <textarea
          id="explanation"
          className={styles.editor__textarea}
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          placeholder="Explain the correct answer and why..."
          rows={4}
          maxLength={2000}
        />
      </div>

      {/* Difficulty */}
      <div className={styles.editor__field}>
        <label htmlFor="difficulty" className={styles.editor__label}>
          Difficulty
        </label>
        <select
          id="difficulty"
          className={styles.editor__select}
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value as Difficulty)}
        >
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      {/* Tags */}
      <div className={styles.editor__field}>
        <label htmlFor="tags" className={styles.editor__label}>
          Tags (comma-separated)
        </label>
        <input
          id="tags"
          type="text"
          className={styles.editor__input}
          value={tags.join(', ')}
          onChange={(e) => setTags(e.target.value.split(',').map((t) => t.trim()).filter(Boolean))}
          placeholder="e.g., history, world-war-2, dates"
        />
      </div>

      {/* Actions */}
      <div className={styles.editor__actions}>
        <motion.button
          className={`${styles.editor__saveButton} ${!isValid ? styles['editor__saveButton--disabled'] : ''}`}
          onClick={handleSave}
          disabled={!isValid}
          whileHover={isValid ? { scale: 1.02 } : {}}
          whileTap={isValid ? { scale: 0.98 } : {}}
        >
          {isEditMode ? 'Update Question' : 'Add Question'}
        </motion.button>
        <motion.button
          className={styles.editor__cancelButton}
          onClick={onCancel}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Cancel
        </motion.button>
      </div>
    </motion.div>
  );
}
